import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { ArrowUpRight } from "lucide-react";
import { SectionOrnament } from "@/components/SectionOrnament";

const OFFERINGS = [
  {
    n: "01",
    slug: "your-journey",
    title: "Your Journey",
    tagline: "1:1 Exploration",
    body:
      "For those seeking greater coherence between their inner world, outer reality and the ripples they create. A personalised exploration of self, patterns, values and direction — supporting emotional, energetic and systemic alignment across life, relationships and work.",
    img: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1200&q=80",
    cta: "Begin",
  },
  {
    n: "02",
    slug: "vision-architecture",
    title: "Vision Architecture",
    tagline: "For visions still becoming",
    body:
      "For those carrying something they cannot yet fully describe, but know wants to emerge. Translating intuition into structure, shaping offerings, frameworks and next steps without losing the soul of the work, energetically, creatively, strategically.",
    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1200&q=80",
    cta: "Architect",
  },
  {
    n: "03",
    slug: "collective-experiences",
    title: "Collective Experiences",
    tagline: "Organisations, teams & communities",
    body:
      "Workshops, retreats and facilitated experiences designed around the unique tensions and questions present in a group. We explore belonging, communication, identity and collective care, strengthening trust, participation and the unseen structures shaping community.",
    img: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/gd8dsrqm_IMG_1570.jpeg",
    cta: "Curate",
  },
  {
    n: "04",
    slug: "framework-story-development",
    title: "Framework & Story Development",
    tagline: "Translating ideas into form",
    body:
      "Working with thinkers and organisations to translate complex ideas into frameworks, narratives and tools that travel — built to be lived with, not just read.",
    img: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/6i2tdaxi_IMG_1564.JPG",
    cta: "Shape it",
  },
  {
    n: "05",
    slug: "speaking-collaborations",
    title: "Speaking & Collaborations",
    tagline: "Shared inquiries",
    body:
      "Spaces exploring how we live, lead, relate and build together. Through reflections, stories and evolving frameworks, I support conversations around harmony, belonging and the relationship between inner worlds and the systems surrounding us. Open to talks, residencies and co-created experiences.",
    img: "https://customer-assets.emergentagent.com/job_systems-echo/artifacts/smjnan05_IMG_1545.JPG",
    cta: "Invite",
  },
];

export const Offerings = () => {
  return (
    <section
      id="offerings"
      data-testid="offerings-section"
      className="relative bg-bone py-28 md:py-40 overflow-hidden"
    >
      <SectionOrnament side="left" vertical="top-32" size={520} opacity={0.07} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.12)}
          className="grid md:grid-cols-12 gap-8 items-end mb-16"
        >
          <div className="md:col-span-7">
            <motion.p
              variants={fadeUp}
              className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-4"
            >
              ✦ &nbsp; 04 , Offerings
            </motion.p>
            <motion.h2
              variants={fadeUp}
              data-testid="offerings-title"
              className="font-serif text-5xl md:text-7xl text-charcoal leading-[0.95] tracking-tight"
            >
              Ways we can
              <br />
              <span className="italic text-bronze">work together.</span>
            </motion.h2>
          </div>
          <motion.p
            variants={fadeUp}
            className="md:col-span-5 text-ink text-base md:text-lg font-light leading-relaxed"
          >
            Every offering is an{" "}
            <span className="italic font-serif text-bronze text-[1.4em]">
              invitation
            </span>{" "}
            to explore, reflect and create new possibilities together. Some
            doors open inward. Some open outward. All begin with conversation.
          </motion.p>
        </motion.div>

        {/* Arched offering cards — each links to /offerings/:slug */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.12)}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-6 items-stretch"
        >
          {OFFERINGS.map((o, i) => (
            <motion.div
              key={o.slug}
              variants={fadeUp}
              data-testid={`offering-${i}`}
              className="h-full"
            >
              <Link
                to={`/offerings/${o.slug}`}
                data-testid={`offering-link-${o.slug}`}
                className="group relative bg-sand border border-stone/30 overflow-hidden flex flex-col h-full hover:bg-clay/30 transition-colors duration-700"
                style={{ borderTopLeftRadius: "180px", borderTopRightRadius: "180px" }}
              >
                {/* Arched image */}
                <div
                  className="zoom-container relative aspect-[3/4] overflow-hidden"
                  style={{ borderTopLeftRadius: "180px", borderTopRightRadius: "180px" }}
                >
                  <img src={o.img} alt={o.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-b from-charcoal/10 via-transparent to-bone/85" />
                  <div className="absolute top-7 left-0 right-0 text-center">
                    <p className="font-serif italic text-bronze text-2xl">{o.n}</p>
                  </div>
                </div>

                {/* Card body */}
                <div className="px-5 pb-7 pt-2 flex-1 flex flex-col text-center">
                  <div className="min-h-[3.75rem] flex items-start justify-center">
                    <h3 className="font-serif text-[1.3rem] md:text-[1.35rem] text-charcoal leading-[1.1]">
                      {o.title}
                    </h3>
                  </div>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-stone min-h-[2rem]">
                    {o.tagline}
                  </p>
                  <div className="my-3 w-8 h-px bg-bronze/40 mx-auto" />
                  <p className="text-[13px] text-ink/85 leading-relaxed font-light flex-1">
                    {o.body}
                  </p>
                  <span
                    data-testid={`offering-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-1.5 justify-center text-[10px] uppercase tracking-[0.3em] text-forest link-underline self-center"
                  >
                    {o.cta}
                    <ArrowUpRight size={12} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="mt-16 text-center font-serif italic text-bronze text-xl md:text-2xl max-w-2xl mx-auto leading-snug"
        >
          Each door is open. Walk through the one that calls you.
        </motion.p>
      </div>
    </section>
  );
};

export default Offerings;
